import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import UploadIcon from '@atlaskit/icon/core/upload';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/default/button';
import Lozenge from '@atlaskit/lozenge/lozenge';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import wordIcon from '../img/word.svg';
import googleDocsIcon from '../img/googleDocs.svg';
import oneDriveIcon from '../img/oneDriveLogo.svg';

import { fg } from '@confluence/feature-gating';

import { useTwcg2304ImportInOnboarding } from '../hooks/useTwcg2304ImportInOnboarding.ts';

export type ImportSource = 'wordDoc' | 'gDoc' | 'office365Doc';

const FEDRAMP_HOSTNAMES = new Set([
	'atlassian-fex.com',
	'atlassian-fex.net',
	'atlassian-us-gov-mod.com',
	'atlassian-us-gov-mod.net',
	'atlassian-stg-fedm.net',
]);

const isFedRampEnv = (): boolean => {
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (!fg('cc_feature_discovery_ifs_fe')) return false;
	const hostname = window?.location?.hostname || '';
	const environmentTail = hostname.split('.').slice(-2).join('.');
	return FEDRAMP_HOSTNAMES.has(environmentTail);
};

const i18n = defineMessages({
	importLabel: {
		id: 'experiment-twcg-2304-import-in-onboarding.import-cta.label',
		defaultMessage: 'Import',
		description: 'Label for the Import CTA button shown in the left nav during onboarding',
	},
	importLozenge: {
		id: 'experiment-twcg-2304-import-in-onboarding.import-cta.lozenge',
		defaultMessage: 'TRY',
		description: 'Lozenge for the Import CTA button shown in the left nav during onboarding',
	},
	microsoftWordLabel: {
		id: 'experiment-twcg-2304-import-in-onboarding.import-cta.source.word',
		defaultMessage: 'Microsoft Word',
		description: 'Dropdown item label for importing a Microsoft Word document',
	},
	googleDocsLabel: {
		id: 'experiment-twcg-2304-import-in-onboarding.import-cta.source.gdoc',
		defaultMessage: 'Google Docs',
		description: 'Dropdown item label for importing a Google Docs document',
	},
	oneDriveLabel: {
		id: 'experiment-twcg-2304-import-in-onboarding.import-cta.source.onedrive',
		defaultMessage: 'OneDrive',
		description: 'Dropdown item label for importing a document from Microsoft OneDrive',
	},
});

const sourceIconStyles = css({
	width: '16px',
	height: '16px',
	objectFit: 'contain',
	display: 'block',
});

const dropdownItemGroupStyles = css({
	minWidth: '242px',
});

const titleWrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.150'),
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.medium'),
	marginLeft: token('space.050'),
});

const buttonWrapperStyles = css({
	height: '36px',
	marginLeft: token('space.0'),
	alignItems: 'center',
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- override button internal padding and correct alignItems regression from @atlaskit/button/new (baseline → center)
	button: {
		padding: '0 8px',
		alignItems: 'center',
	},
});

/**
 * "Import" CTA rendered in the space side navigation for new users.
 *
 * This component is 100% gated behind the twcg-2304-import-in-onboarding experiment.
 * It renders nothing when:
 * - The experiment is loading
 * - The experiment is off (control arm)
 * - The user is not eligible (not founding admin, or outside first 30 days)
 *
 * Clicking the button opens a dropdown with three import source options:
 * Microsoft Word, Google Docs, and OneDrive. Selecting a source calls
 * `onSelectSource` with the corresponding source key.
 *
 * Styled to match the TreeCreateContentButton ("+ Create" button) in the page tree.
 */
export const ImportOnboardingCTA = ({
	onSelectSource,
}: {
	onSelectSource: (source: ImportSource) => void;
}): React.JSX.Element | null => {
	const { isEnabled, loading } = useTwcg2304ImportInOnboarding();
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isFedRamp = isFedRampEnv();

	if (loading || !isEnabled) {
		return null;
	}

	return (
		<div css={buttonWrapperStyles}>
			<DropdownMenu
				placement="bottom-start"
				onOpenChange={({ isOpen }) => {
					if (!isOpen) return;

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'clicked',
							actionSubject: 'button',
							source: 'twcg-2304-import-in-onboarding-import-button',
						},
					}).fire();
				}}
				trigger={({ triggerRef, onClick, ...triggerProps }) => (
					<Button
						ref={triggerRef}
						onClick={onClick}
						testId="import-onboarding-cta"
						iconBefore={UploadIcon}
						appearance="subtle"
						shouldFitContainer
						{...triggerProps}
					>
						<span css={titleWrapperStyles}>
							{intl.formatMessage(i18n.importLabel)}
							<Lozenge appearance="new" isBold>
								{intl.formatMessage(i18n.importLozenge)}
							</Lozenge>
						</span>
					</Button>
				)}
			>
				<DropdownItemGroup css={dropdownItemGroupStyles}>
					<DropdownItem
						testId="import-onboarding-cta-word"
						onClick={() => onSelectSource('wordDoc')}
						elemBefore={<img css={sourceIconStyles} src={wordIcon} alt="" aria-hidden />}
					>
						{intl.formatMessage(i18n.microsoftWordLabel)}
					</DropdownItem>
					{!isFedRamp && (
						<DropdownItem
							testId="import-onboarding-cta-gdoc"
							onClick={() => onSelectSource('gDoc')}
							elemBefore={<img css={sourceIconStyles} src={googleDocsIcon} alt="" aria-hidden />}
						>
							{intl.formatMessage(i18n.googleDocsLabel)}
						</DropdownItem>
					)}
					{!isFedRamp && (
						<DropdownItem
							testId="import-onboarding-cta-onedrive"
							onClick={() => onSelectSource('office365Doc')}
							elemBefore={<img css={sourceIconStyles} src={oneDriveIcon} alt="" aria-hidden />}
						>
							{intl.formatMessage(i18n.oneDriveLabel)}
						</DropdownItem>
					)}
				</DropdownItemGroup>
			</DropdownMenu>
		</div>
	);
};
